import _ from "lodash";
import moment from "moment";
import styled from "@emotion/styled";
import JdBadge from "@components/JdBadge";
import { Column, Row } from "@src/components/Flex";
import React, { useState } from "react";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import * as dot from "@components/Dot";
import { deviceFreshness } from "@src/utils/deviceOnline";
import { DeviceType, TDevice } from "@services/devicesService";
import { useDevicesScreenVM } from "@screens/DevicesScreen/DevicesScreenVM";
import Button from "@components/Button";
import { observer } from "mobx-react-lite";
import { useLang } from "@src/hooks/useLang";
import DeviceFarmTags from "@screens/DevicesScreen/DeviceFarmTags";
import { Link } from "react-router-dom";
import { ROUTES } from "@stores/AccountStore";
import Sparkline from "@components/Sparkline";
import ActivityGrid from "@components/ActivityGrid";
import Tooltip from "@components/Tooltip";
import useIsMobile from "@src/hooks/useIsMobile";
import { JD_CONNECTIONS_URL, johnDeereProblem } from "@screens/DevicesScreen/johnDeereStatus";

interface IProps {
  device: TDevice;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  padding: 16px;
  box-sizing: border-box;
  min-height: 94px;
  transition: 0.4s;
  background: #fff;
  border-bottom: 1px solid rgba(137, 144, 147, 0.3);
  position: relative;
  :hover {
    background: #f8f8f8;
    .plus-button {
      background: #fff;
    }
  }
`;

// Button defaults to width:100%, so anything sitting in a row needs pinning.
// The generous 28px side padding is what pushed Calibrate off-screen on a
// phone, so the mobile variant below trades it for equal-width buttons.
// The same green John Deere badge as imported field boundaries (FieldRow),
// one size up to sit beside the 18 px machine name.
const SourceTag = styled(JdBadge)`
  margin-left: 0;
  padding: 1px 8px;
  font-size: 12px;
  line-height: 18px;
`;

const RowButton = styled(Button)`
  max-width: fit-content;
  height: 36px;
  padding: 0 28px;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
`;

// Secondary to Settings/Calibrate: a small bordered chip rather than a third
// full-size button, so the action row keeps two visual weights instead of three
// and stays inside the width budget the charts already compete for.
const SideButton = styled.button`
  height: 28px;
  padding: 0 10px;
  border: 1px solid rgba(104, 173, 24, 0.4);
  border-radius: 4px;
  background: none;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: #68ad18;
  cursor: pointer;
  white-space: nowrap;
  transition: 0.2s;
  &:hover {
    background: rgba(104, 173, 24, 0.08);
    border-color: #68ad18;
  }
`;

const MobileButton = styled(Button)`
  height: 36px;
  padding: 0 12px;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
`;

// Each button gets half the row on mobile, so neither can overflow no matter
// how long its translation is.
const MobileButtonRow = styled(Row)`
  align-items: center;
  gap: 8px;
  > * {
    flex: 1 1 0;
    min-width: 0;
  }
  a {
    display: block;
  }
`;

const DetailsToggle = styled.button`
  border: none;
  background: none;
  padding: 4px 0;
  font-size: 14px;
  line-height: 20px;
  color: #68ad18;
  cursor: pointer;
  white-space: nowrap;
`;

// John Deere combines: when harvest data last came in, and what stopped it.
const JdStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 12px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: #899093;
  a {
    color: #68ad18;
    text-decoration: underline;
    white-space: nowrap;
  }
`;

const JdProblemText = styled.span`
  color: #b45309;
`;

// Charts can be wider than a phone; let them scroll rather than stretch the row.
const ChartScroller = styled.div`
  width: 100%;
  overflow-x: auto;
`;

// Between the mobile breakpoint and a comfortable desktop there is still not
// room for tags + a year grid + a trend + two buttons. Drop the charts in that
// band rather than let them squeeze the buttons off the edge again — widest
// goes first. The buttons are the part that must never be unreachable.
const GridSlot = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: 1150px) {
    display: none;
  }
`;

const SparkSlot = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: 920px) {
    display: none;
  }
`;

// Same freshness rule as the Tracks device menu and map (deviceFreshness).
const StatusDot: React.FC<{ lastSeenAt?: string | null }> = ({ lastSeenAt }) => (
  <dot.FreshnessDot freshness={deviceFreshness(lastSeenAt)} />
);

const DeviceRow: React.FC<IProps> = ({ device }) => {
  const lang = useLang();
  const vm = useDevicesScreenVM();
  const isMobile = useIsMobile();
  const t = (lang.devices ?? {}) as any;
  const [expanded, setExpanded] = useState(false);

  const handleEdit = () => {
    vm.setEditedDevice(device);
    vm.setCreateDialogOpened(true);
  };
  const lastSeen = device.lastSeenAt ? moment(device.lastSeenAt) : null;
  const days = vm.healthFor(device.id);
  const broken = days.filter((d) => d.severity !== "ok").length;

  const activity = vm.activityFor(device.id);
  const workedDays = Object.keys(activity).length;
  const totalHours = Math.round(
    Object.values(activity).reduce((a, s) => a + s, 0) / 3600
  );
  const activityTooltip =
    workedDays === 0
      ? t.gridNoData ?? "No harvest recorded yet in the last year"
      : (t.gridSummary ?? "{hours} h harvested across {days} days")
          .replace("{hours}", totalHours.toLocaleString())
          .replace("{days}", String(workedDays));
  const healthTooltip =
    days.length === 0
      ? t.healthNoData ??
        "No sensor history yet. It builds up daily once the machine reports."
      : broken > 0
      ? (t.healthBrokenDays ?? "{n} of the last {total} days had a sensor fault")
          .replace("{n}", String(broken))
          .replace("{total}", String(days.length))
      : (t.healthAllClear ?? "No sensor faults in the last {total} days").replace(
          "{total}",
          String(days.length)
        );

  const grid = (
    <Tooltip content={activityTooltip}>
      <ActivityGrid days={activity} span={vm.activityDays} />
    </Tooltip>
  );
  // Always rendered, including with no data: the empty state is a grey dashed
  // line, which says "no sensor history yet" far better than an unexplained
  // gap where other rows have a chart. Grey once the device has been quiet for
  // over a day — same threshold as the status dot above.
  const spark = (
    <Tooltip content={healthTooltip}>
      <Sparkline
        days={days}
        channel={vm.healthChannelFor(device.id)}
        muted={["stale", "never"].includes(deviceFreshness(device.lastSeenAt))}
      />
    </Tooltip>
  );

  // Opens the same edit dialog that clicking the row does — this is just the
  // discoverable way in. stopPropagation keeps the row's own onClick from
  // firing it a second time.
  const onSettings = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleEdit();
  };
  const calibrateTo = ROUTES.DEVICES_CALIBRATION.replace(":id", device.id);

  // Potato machines only: the page charts the potato sensor/frequency pair, and
  // the endpoint behind it 404s for anything else.
  const isPotato = device.deviceType === DeviceType.DEVICE_TYPE_POTATO;
  // Imported from John Deere Operations Center: harvest data arrives in
  // batches and there is no GreenGrowth sensor, so the sensor-health
  // sparkline is meaningless and is not shown. The harvest grid still is.
  const isJohnDeere = device.source === "johndeere";
  const openTechnical = (e: React.MouseEvent) => {
    // The row itself opens the edit dialog, so every control on it has to stop
    // propagation. Own tab, so the customer can keep the machine's telemetry up
    // beside the map while they work.
    e.stopPropagation();
    // Deliberately NOT "noopener". sessionStorage is copied into a new tab only
    // when it is opened WITH an opener; `noopener` gives the tab a fresh, empty
    // one. Admin impersonation lives in sessionStorage on purpose (per-tab, so
    // it cannot leak between windows), so with noopener the page loaded as the
    // admin's own account and 404'd on someone else's device. Same-origin route
    // in our own app, so the opener reference costs nothing here.
    window.open(ROUTES.DEVICE_TECHNICAL.replace(":id", device.id), "_blank");
  };

  // Contract C (GET /devices `johnDeere`). The import is the only way data
  // reaches this machine, so a stopped import is said here, with the fix.
  const jdProblem = johnDeereProblem(device.johnDeere);
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const jdStatus =
    isJohnDeere && device.johnDeere ? (
      <JdStatus>
        <span>
          {device.johnDeere.lastImportAt
            ? (t.jdLastImport ?? "Last John Deere import {time}").replace(
                "{time}",
                moment(device.johnDeere.lastImportAt).fromNow()
              )
            : t.jdNoImportYet ?? "Waiting for the first John Deere import"}
        </span>
        {jdProblem && (
          <JdProblemText>
            {t[jdProblem.key] ?? jdProblem.fallback}{" "}
            {jdProblem.fix === "deereConnections" && (
              <a href={JD_CONNECTIONS_URL} target="_blank" rel="noopener noreferrer" onClick={stop}>
                {t.jdOpenConnections ?? "Open connections.deere.com"}
              </a>
            )}
            {jdProblem.fix === "settings" && (
              <Link to={ROUTES.SETTINGS} onClick={stop}>
                {t.jdOpenSettings ?? "Open Settings"}
              </Link>
            )}
          </JdProblemText>
        )}
      </JdStatus>
    ) : null;

  const header = (
    <Row style={{ marginBottom: jdStatus ? 4 : 16, flexWrap: "wrap", rowGap: 4 }} alignItems="center">
      <StatusDot lastSeenAt={device.lastSeenAt} />
      <SizedBox width={8} />
      <Text size={18} color="#494C5B" type="heading" fitContent>
        {_.isEmpty(device.harvester.name)
          ? device.esn.toUpperCase()
          : device.harvester.name}
      </Text>
      <SizedBox width={4} />
      <Text size={14} color="#CBD5D8" type="heading" fitContent>
        •
      </Text>
      <SizedBox width={4} />
      <Text size={18} color="#899093" type="heading" fitContent>
        {device.harvester.model}
      </Text>
      {isJohnDeere && (
        <>
          <SizedBox width={10} />
          <SourceTag>{lang.devices.johnDeereTag}</SourceTag>
        </>
      )}
      {lastSeen && lastSeen.isValid() && (
        <>
          <SizedBox width={10} />
          <Text size={13} color="#B4B7C0" fitContent>
            {lastSeen.fromNow()}
          </Text>
        </>
      )}
    </Row>
  );

  // Phone: there is no room for tags, two charts and two buttons on one line —
  // that is what was clipping Calibrate off the screen. Stack instead, and keep
  // the charts behind a toggle so the default row stays short. They are not
  // mounted while collapsed, so a 365-cell grid costs nothing on a phone.
  if (isMobile) {
    return (
      <Root onClick={handleEdit}>
        {header}
        {jdStatus}
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <Row justifyContent="space-between" alignItems="center">
            <DeviceFarmTags device={device} />
            <DetailsToggle
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
            >
              {expanded
                ? t.hideDetails ?? "Hide details"
                : t.showDetails ?? "Details"}
            </DetailsToggle>
          </Row>

          {expanded && (
            <Column crossAxisSize="max" style={{ gap: 12 }}>
              <ChartScroller>{grid}</ChartScroller>
              {!isJohnDeere && <ChartScroller>{spark}</ChartScroller>}
              {isPotato && (
                <SideButton
                  onClick={openTechnical}
                  style={{ alignSelf: "flex-start" }}
                >
                  {t.technicalStatusButton ?? "Technical status"}
                </SideButton>
              )}
            </Column>
          )}

          <MobileButtonRow>
            <MobileButton kind="outline" onClick={onSettings}>
              {t.detailsButton ?? "Settings"}
            </MobileButton>
            <Link to={calibrateTo} onClick={(e) => e.stopPropagation()}>
              <MobileButton>{lang.devices.calibrateButton}</MobileButton>
            </Link>
          </MobileButtonRow>
        </Column>
      </Root>
    );
  }

  return (
    <Root onClick={handleEdit}>
      {header}
      {jdStatus}
      <Row justifyContent="space-between" alignItems="center">
        <DeviceFarmTags device={device} />
        <Row alignItems="center" mainAxisSize="fit-content">
          {/* Harvest activity — a year of day cells, GitHub-style. Sits between
              the farm tags and the sensor trend, where the row has spare width. */}
          <GridSlot>
            {grid}
            <SizedBox width={16} />
          </GridSlot>
          {!isJohnDeere && (
            <SparkSlot>
              {spark}
              <SizedBox width={16} />
            </SparkSlot>
          )}
          {isPotato && (
            <>
              <SideButton onClick={openTechnical}>
                {t.technicalStatusButton ?? "Technical status"}
              </SideButton>
              <SizedBox width={12} />
            </>
          )}
          <RowButton kind="outline" onClick={onSettings}>
            {t.detailsButton ?? "Settings"}
          </RowButton>
          <SizedBox width={8} />
          <Link to={calibrateTo} onClick={(e) => e.stopPropagation()}>
            <RowButton>{lang.devices.calibrateButton}</RowButton>
          </Link>
        </Row>
      </Row>
    </Root>
  );
};
export default observer(DeviceRow);
